import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export interface UserWithRole {
  id: string;
  phone_number: string;
  full_name: string | null;
  email: string | null;
  branch: string | null;
  branch_id?: number | null;
  branch_ids?: number[];
  status: string;
  created_at: string;
  user_roles: Array<{ role: string }>;
  profile_branches?: Array<{ branch_id: number }>;
}

export interface CreateUserData {
  phone_number: string;
  full_name: string;
  email?: string;
  role: string;
  branch?: string;
  branch_id?: number;
  branch_ids?: number[];
  department?: string;
  designation?: string;
}

export interface UpdateUserData {
  full_name?: string;
  email?: string;
  branch?: string;
  branch_id?: number;
  status?: string;
  department?: string;
  designation?: string;
}

export const useUserManagement = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  // Fetch all users with roles
  const fetchUsers = async (branchId?: number | null) => {
    if (branchId === undefined && currentBranchId == null) {
      return { data: [], error: null };
    }
    const branchIdToUse = branchId === undefined ? currentBranchId ?? null : branchId;
    // NOTE: `profile_branches` is present in DB but missing in generated client types.
    // Use a localized `any` cast to keep runtime behavior while types are out of sync.
    let query = (supabase as any)
      .from('profiles')
      .select(`
        id,
        phone_number,
        full_name,
        email,
        branch,
        branch_id,
        status,
        created_at,
        profile_branches!inner(branch_id)
      `)
      .order('created_at', { ascending: false });

    if (branchIdToUse != null) {
      query = query.eq('profile_branches.branch_id', branchIdToUse);
    }

    const { data, error } = await query;
    
    if (error) return { data: null, error };
    
    // Fetch roles separately for each user
    const usersWithRoles = await Promise.all(
      ((data as any[]) || []).map(async (profile: any) => {
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', profile.id);
        
        return {
          ...profile,
          user_roles: roles || [],
          branch_ids: (profile as any).profile_branches?.map((pb: any) => pb.branch_id) || [],
        };
      })
    );
    
    return { data: usersWithRoles as UserWithRole[], error: null };
  };

  // Add user (admin only) - calls edge function
  const addUser = async (userData: CreateUserData) => {
    if (currentBranchId == null && userData.branch_id == null && (!userData.branch_ids || userData.branch_ids.length === 0)) {
      return { data: null, error: new Error("Branch not selected") };
    }
    const branchIds =
      userData.branch_ids && userData.branch_ids.length > 0
        ? userData.branch_ids
        : [userData.branch_id ?? currentBranchId ?? undefined].filter((v): v is number => typeof v === "number");
    
    // Read custom session token (set by login edge function).
    // Prefer localStorage, fallback to sessionStorage for older flows.
    let sessionToken: string | undefined;
    try {
      const authDataRaw = typeof window !== "undefined" ? localStorage.getItem("customAuth") : null;
      if (authDataRaw) {
        const parsed = JSON.parse(authDataRaw);
        sessionToken = typeof parsed?.sessionToken === "string" ? parsed.sessionToken : undefined;
      }
      if (!sessionToken && typeof window !== "undefined") {
        const sessionStorageToken = sessionStorage.getItem("sessionToken");
        sessionToken = sessionStorageToken || undefined;
      }
    } catch {
      if (typeof window !== "undefined") {
        const sessionStorageToken = sessionStorage.getItem("sessionToken");
        sessionToken = sessionStorageToken || undefined;
      } else {
        sessionToken = undefined;
      }
    }

    const payload = {
      ...userData,
      full_name: userData.full_name?.trim(),
      email: userData.email?.trim() ? userData.email.trim() : null,
      department: userData.department?.trim() ? userData.department.trim() : null,
      designation: userData.designation?.trim() ? userData.designation.trim() : null,
      branch_id: userData.branch_id ?? currentBranchId ?? undefined,
      branch_ids: branchIds,
      organization_id: organizationId,
    };

    // Also include the current Supabase access token explicitly, so the edge function
    // can reliably resolve requester identity even if the custom session token is missing.
    const {
      data: sessionData,
    } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!sessionToken && !accessToken) {
      return { data: null, error: new Error("Session expired. Please login again.") };
    }

    const headers: Record<string, string> = {};
    if (sessionToken) {
      headers["x-session-token"] = sessionToken;
    }
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const { data, error } = await supabase.functions.invoke('create-user', {
      body: {
        ...payload,
      },
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
    
    return { data, error };
  };

  // Update user profile
  const updateUser = async (userId: string, updates: UpdateUserData) => {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);
      
    return { error };
  };

  // Update user role
  const updateUserRole = async (userId: string, newRole: string) => {
    // Delete old roles first
    const { error: deleteError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId);
    
    if (deleteError) return { error: deleteError };
      
    // Insert new role
    const { error } = await supabase
      .from('user_roles')
      .insert({
        user_id: userId,
        role: newRole
      } as any);
      
    return { error };
  };

  // Delete user (soft delete by setting status to inactive)
  const deleteUser = async (userId: string) => {
    const { error } = await supabase
      .from('profiles')
      .update({ status: 'inactive' })
      .eq('id', userId);
      
    return { error };
  };

  const updateUserBranches = async (userId: string, branchIds: number[]) => {
    const { error: deleteError } = await (supabase as any)
      .from('profile_branches')
      .delete()
      .eq('profile_id', userId);
    if (deleteError) return { error: deleteError };

    if (branchIds.length > 0) {
      const { error: insertError } = await (supabase as any)
        .from('profile_branches')
        .insert(branchIds.map((branchId: number) => ({
          profile_id: userId,
          branch_id: branchId,
        })));
      if (insertError) return { error: insertError };
    }

    return { error: null };
  };

  return { fetchUsers, addUser, updateUser, updateUserRole, deleteUser, updateUserBranches };
};
